import { io, Socket } from 'socket.io-client';
import config from '../config';

interface MessageEvent {
  id: number;
  uuid: string;
  content: string;
  created_at: string;
  sender_id: number;
  sender_name: string;
  sender_avatar: string;
  group_id: number;
  mentions: any[];
}

interface WebSocketError {
  message: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isConnected = false;
  private messageListeners: Map<string, Set<(message: MessageEvent) => void>> = new Map();
  private errorListeners: Set<(error: WebSocketError) => void> = new Set();
  private connectionListeners: Set<(connected: boolean) => void> = new Set();
  private typingListeners: Set<(data: any) => void> = new Set();
  private readReceiptListeners: Set<(data: any) => void> = new Set();
  private clockListeners: Map<string, Set<(data: any) => void>> = new Map();
  private frameioListeners: Map<string, Set<(data: any) => void>> = new Map();
  private debug = true; // Temporarily enabled for debugging

  constructor() {
    // Don't auto-connect on construction - wait for explicit connect() call
    if (this.debug) {
      console.log('WebSocket service initialized (not connected yet)');
    }
  }

  private initializeConnection() {
    const token = this.getAuthToken();
    if (!token) {
      console.warn('No auth token found, skipping WebSocket connection');
      return;
    }

    // Use BASE_URL if available, otherwise derive from API_URL
    const baseUrl = config.api.BASE_URL || config.api.API_URL.replace('/api', '');
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    
    if (this.debug) {
      console.log('WebSocket connection details:', {
        wsUrl,
        tokenPresent: !!token,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'none',
        apiUrl: config.api.API_URL,
        baseUrl: config.api.BASE_URL,
        derivedWsUrl: wsUrl
      });
    }
    
    this.socket = io(wsUrl, {
      path: '/ws',
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000
    });

    this.setupEventListeners();
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      if (this.debug) {
        console.log('WebSocket connected');
      }
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyConnectionListeners(true);
    });

    this.socket.on('disconnect', (reason) => {
      if (this.debug) {
        console.log('WebSocket disconnected:', reason);
      }
      this.isConnected = false;
      this.notifyConnectionListeners(false);
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('WebSocket connection error:', error.message);
      this.isConnected = false;
      this.notifyConnectionListeners(false);
      
      // Don't notify about auth errors - those are expected when not logged in
      if (error.message !== 'No token provided' && error.message !== 'Invalid token') {
        this.notifyErrorListeners({ message: 'Connection failed: ' + error.message });
      }
      
      // If it's an auth error, clear the socket to force fresh connection later
      if (error.message === 'Invalid token' || error.message === 'Token expired' || error.message === 'No token provided') {
        if (this.debug) {
          console.log('Auth error detected - clearing socket instance');
        }
        setTimeout(() => {
          if (this.socket && !this.socket.connected) {
            this.socket.disconnect();
            this.socket = null;
          }
        }, 100);
      }
    });

    this.socket.on('new_message', (message: MessageEvent) => {
      if (this.debug) {
        console.log('New message received:', message);
      }
      this.notifyMessageListeners(message.group_id.toString(), message);
      
      // Also notify the wildcard listeners for unread count updates
      this.notifyMessageListeners('*', message);
    });

    this.socket.on('error', (error: WebSocketError) => {
      console.error('WebSocket error:', error);
      this.notifyErrorListeners(error);
    });

    // Listen for typing indicators
    this.socket.on('user_typing', (data: any) => {
      if (this.debug) {
        console.log('User typing event:', data);
      }
      this.notifyTypingListeners(data);
    });

    // Listen for read receipts
    this.socket.on('messages_read_by_user', (data: any) => {
      if (this.debug) {
        console.log('Messages read by user event:', data);
      }
      this.notifyReadReceiptListeners(data);
    });

    // Listen for clock events
    this.socket.on('clock_started', (data: any) => {
      if (this.debug) {
        console.log('Clock started event:', data);
      }
      this.notifyClockListeners('started', data);
    });

    this.socket.on('clock_stopped', (data: any) => {
      if (this.debug) {
        console.log('Clock stopped event:', data);
      }
      this.notifyClockListeners('stopped', data);
    });

    // Listen for pause events
    this.socket.on('pause_started', (data: any) => {
      if (this.debug) {
        console.log('Pause started event:', data);
      }
      this.notifyClockListeners('pause_started', data);
    });

    this.socket.on('pause_ended', (data: any) => {
      if (this.debug) {
        console.log('Pause ended event:', data);
      }
      this.notifyClockListeners('pause_ended', data);
    });

    // Listen for Frame.io events
    this.socket.on('frameio:metadata:updated', (data: any) => {
      console.log('🔄 Frame.io metadata updated:', data);
      this.notifyFrameioListeners('metadata:updated', data);
    });

    this.socket.on('frameio:comment:created', (data: any) => {
      console.log('💬 Frame.io comment created:', data);
      this.notifyFrameioListeners('comment:created', data);
    });

    this.socket.on('frameio:comment:updated', (data: any) => {
      console.log('✏️ Frame.io comment updated:', data);
      this.notifyFrameioListeners('comment:updated', data);
    });

    this.socket.on('frameio:file:updated', (data: any) => {
      console.log('📁 Frame.io file updated:', data);
      this.notifyFrameioListeners('file:updated', data);
    });

    this.socket.on('frameio:file:ready', (data: any) => {
      console.log('✅ Frame.io file ready:', data);
      this.notifyFrameioListeners('file:ready', data);
    });

    this.socket.on('frameio:project:updated', (data: any) => {
      console.log('📂 Frame.io project updated:', data);
      this.notifyFrameioListeners('project:updated', data);
    });

    // Listen for new group creation
    this.socket.on('join_new_group', (data: { groupId: number }) => {
      console.log('Received join_new_group event for group:', data.groupId);
      this.joinGroup(data.groupId);
    });

    // Listen for new group creation events
    this.socket.on('join_new_group', (groupId: number) => {
      if (this.debug) {
        console.log('Received join_new_group event for group:', groupId);
      }
      // Automatically join the new group
      this.joinGroup(groupId);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      if (this.debug) {
        console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
      }
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyConnectionListeners(true);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.notifyErrorListeners({ message: 'Connection lost' });
      }
    });
  }

  private getAuthToken(): string | null {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        return parsed.token || parsed.data?.token || null;
      } catch (error) {
        console.error('Error parsing authUser from sessionStorage:', error);
        return null;
      }
    }
    return null;
  }

  private notifyMessageListeners(groupId: string, message: MessageEvent) {
    // Notify specific group listeners
    const listeners = this.messageListeners.get(groupId);
    if (listeners) {
      listeners.forEach(listener => listener(message));
    }
    
    // Notify wildcard listeners (for all groups)
    const wildcardListeners = this.messageListeners.get('*');
    if (wildcardListeners) {
      wildcardListeners.forEach(listener => listener(message));
    }
  }

  private notifyErrorListeners(error: WebSocketError) {
    this.errorListeners.forEach(listener => listener(error));
  }

  private notifyConnectionListeners(connected: boolean) {
    this.connectionListeners.forEach(listener => listener(connected));
  }

  // Public API
  public connect() {
    const token = this.getAuthToken();
    
    // Always check for fresh token
    if (!token) {
      console.warn('Cannot connect WebSocket - no auth token available');
      return;
    }
    
    // If already connected with same token, skip
    if (this.socket?.connected) {
      if (this.debug) {
        console.log('WebSocket already connected', {
          socketId: this.socket.id,
          transport: (this.socket as any).io?.engine?.transport?.name,
          isConnected: this.isConnected
        });
      }
      return;
    }
    
    // Disconnect existing socket if any
    if (this.socket) {
      if (this.debug) {
        console.log('Disconnecting existing WebSocket before reconnecting');
      }
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Initialize new connection
    this.initializeConnection();
  }

  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.notifyConnectionListeners(false);
    }
  }

  public joinGroup(groupId: number) {
    if (this.socket && this.isConnected) {
      if (this.debug) {
        console.log(`Emitting join_group event for group ${groupId}`);
      }
      this.socket.emit('join_group', groupId);
    } else {
      if (this.debug) {
        console.warn(`Cannot join group ${groupId} - WebSocket not connected`);
      }
    }
  }

  public leaveGroup(groupId: number) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave_group', groupId);
    }
  }

  public isSocketConnected(): boolean {
    const connected = this.socket?.connected === true;
    
    // Sync internal state with actual socket state
    if (this.isConnected !== connected) {
      console.warn(`WebSocket state mismatch - internal: ${this.isConnected}, actual: ${connected}`);
      this.isConnected = connected;
      this.notifyConnectionListeners(connected);
    }
    
    return connected;
  }

  // Event listeners
  public onMessage(groupId: string, callback: (message: MessageEvent) => void) {
    if (!this.messageListeners.has(groupId)) {
      this.messageListeners.set(groupId, new Set());
    }
    this.messageListeners.get(groupId)!.add(callback);
  }

  public offMessage(groupId: string, callback: (message: MessageEvent) => void) {
    const listeners = this.messageListeners.get(groupId);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.messageListeners.delete(groupId);
      }
    }
  }

  public onError(callback: (error: WebSocketError) => void) {
    this.errorListeners.add(callback);
  }

  public offError(callback: (error: WebSocketError) => void) {
    this.errorListeners.delete(callback);
  }

  public onConnection(callback: (connected: boolean) => void) {
    this.connectionListeners.add(callback);
    
    // Immediately notify the new listener of current connection status
    if (this.socket?.connected) {
      callback(true);
    }
  }

  public offConnection(callback: (connected: boolean) => void) {
    this.connectionListeners.delete(callback);
  }

  // Typing indicator methods
  public onTyping(callback: (data: any) => void) {
    this.typingListeners.add(callback);
  }

  public offTyping(callback: (data: any) => void) {
    this.typingListeners.delete(callback);
  }

  public sendTyping(groupId: number, isTyping: boolean) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { groupId, isTyping });
      if (this.debug) {
        console.log('Sent typing event:', { groupId, isTyping });
      }
    }
  }

  // Read receipt methods
  public onReadReceipt(callback: (data: any) => void) {
    this.readReceiptListeners.add(callback);
  }

  public offReadReceipt(callback: (data: any) => void) {
    this.readReceiptListeners.delete(callback);
  }

  public sendMessageRead(messageIds: number[], groupId: number) {
    if (this.socket?.connected) {
      this.socket.emit('message_read', { messageIds, groupId });
      if (this.debug) {
        console.log('Sent message read event:', { messageIds, groupId });
      }
    }
  }

  // Clock event methods
  public onClockStarted(callback: (data: any) => void) {
    if (!this.clockListeners.has('started')) {
      this.clockListeners.set('started', new Set());
    }
    this.clockListeners.get('started')!.add(callback);
  }

  public offClockStarted(callback: (data: any) => void) {
    const listeners = this.clockListeners.get('started');
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.clockListeners.delete('started');
      }
    }
  }

  public onClockStopped(callback: (data: any) => void) {
    if (!this.clockListeners.has('stopped')) {
      this.clockListeners.set('stopped', new Set());
    }
    this.clockListeners.get('stopped')!.add(callback);
  }

  public offClockStopped(callback: (data: any) => void) {
    const listeners = this.clockListeners.get('stopped');
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.clockListeners.delete('stopped');
      }
    }
  }

  // Pause event methods
  public onPauseStarted(callback: (data: any) => void) {
    if (!this.clockListeners.has('pause_started')) {
      this.clockListeners.set('pause_started', new Set());
    }
    this.clockListeners.get('pause_started')!.add(callback);
  }

  public offPauseStarted(callback: (data: any) => void) {
    const listeners = this.clockListeners.get('pause_started');
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.clockListeners.delete('pause_started');
      }
    }
  }

  public onPauseEnded(callback: (data: any) => void) {
    if (!this.clockListeners.has('pause_ended')) {
      this.clockListeners.set('pause_ended', new Set());
    }
    this.clockListeners.get('pause_ended')!.add(callback);
  }

  public offPauseEnded(callback: (data: any) => void) {
    const listeners = this.clockListeners.get('pause_ended');
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.clockListeners.delete('pause_ended');
      }
    }
  }

  private notifyTypingListeners(data: any) {
    this.typingListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in typing listener:', error);
      }
    });
  }

  private notifyReadReceiptListeners(data: any) {
    this.readReceiptListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in read receipt listener:', error);
      }
    });
  }

  private notifyClockListeners(eventType: string, data: any) {
    const listeners = this.clockListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in clock ${eventType} listener:`, error);
        }
      });
    }
  }

  private notifyFrameioListeners(eventType: string, data: any) {
    const listeners = this.frameioListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in Frame.io ${eventType} listener:`, error);
        }
      });
    }
  }

  // Frame.io event methods
  public onFrameioUpdate(eventType: string, callback: (data: any) => void) {
    if (!this.frameioListeners.has(eventType)) {
      this.frameioListeners.set(eventType, new Set());
    }
    this.frameioListeners.get(eventType)!.add(callback);
  }

  public offFrameioUpdate(eventType: string, callback: (data: any) => void) {
    const listeners = this.frameioListeners.get(eventType);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.frameioListeners.delete(eventType);
      }
    }
  }

  public reconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
    this.initializeConnection();
  }
  
  // Debug method
  public getDebugInfo() {
    return {
      hasSocket: !!this.socket,
      socketConnected: this.socket?.connected,
      socketId: this.socket?.id,
      internalIsConnected: this.isConnected,
      transport: (this.socket as any)?.io?.engine?.transport?.name,
      authToken: !!this.getAuthToken(),
      listeners: {
        messages: this.messageListeners.size,
        errors: this.errorListeners.size,
        connections: this.connectionListeners.size
      }
    };
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();

// Make it available globally for debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).webSocketService = webSocketService;
}

export default webSocketService;